import DeviceManager from '../utils/device-manager';

type AppearItemType = { $item: HTMLElement, observer: IntersectionObserver };

type AppearType = {
    items: AppearItemType[],
    desktopInitialized: boolean,
    init(): void,
    initAppear(attr: AppearItemType): void,
    destroyAppear(attr: AppearItemType): void,
};

/**
 * Usage:
 * 1. Add the class "appear" to your element and optionally add theses attributes:
 *     - appear-threshold          (float)     to override the default threshold (default: 0.3).
 *     - appear-reset              (bool: 0/1) to reset animation when element is not visible in the windows (default: 0).
 *     - appear-enable-mobile      (bool: 0/1) to enable or disable appear animation on mobile device (default: 0).
 *     - appear-mobile-threshold   (float)     to override the default threshold in mobile (default: 0.3).
 * 2. Create the styles on your elements
 *     - .appear is the default class
 *     - .appear-animation is added when animation start
 * 
 * <div class="my-element appear" appear-threshold="0.2" appear-reset="0"></div>
 * 
 * .my-element.appear {
 *     opacity: 0;
 *     transform: translateY(100px);
 *     transition: 1s ease-in-out;
 * }
 * 
 * .my-element.appear.appear-animation {
 *     opacity: 1;
 *     transform: translateY(0);
 * }
 */
const Appear: AppearType = {
    items: [],
    desktopInitialized: false,

    init() : void {
        const $items = document.querySelectorAll<HTMLElement>('.appear');
        const that = this;

        $items.forEach($item => {
            let threshold = 0.3;
            let reset = false;
            let enableMobile = false;
            let mobileThreshold = 0.3;

            const appearThresholdAttr = parseFloat($item.getAttribute('appear-threshold') || '0');
            const resetAttr = parseInt($item.getAttribute('appear-reset') || '0');
            const enableMobileAttr = parseInt($item.getAttribute('appear-enable-mobile') || '0');
            const mobileThresholdAttr = parseInt($item.getAttribute('appear-threshold-attr') || '0');

            threshold = appearThresholdAttr >= 0 ? appearThresholdAttr : threshold;
            reset = resetAttr === 1 ? true : false;
            enableMobile = enableMobileAttr === 1 ? true : false;
            mobileThreshold = mobileThresholdAttr >= 0 ? mobileThresholdAttr : mobileThreshold;

            const observer = new IntersectionObserver(entries => {
                const isMobile = !DeviceManager.isGreaterThanOrEqual(DeviceManager.getCurrentDevice(), 'md');
                const actualThreshold = isMobile ? mobileThreshold : threshold;

                if (entries[0].intersectionRatio > actualThreshold) {
                    that.items.forEach(itemAttr => {
                        if (itemAttr.$item === entries[0].target && !itemAttr.$item.classList.contains('appear-animation')) {
                            itemAttr.$item.classList.add('appear-animation');

                            if (!reset) {
                                observer.unobserve(itemAttr.$item);
                            }
                        }
                    });
                } else if (reset && entries[0].intersectionRatio < 0.1) {
                    that.items.forEach(itemAttr => {
                        if (itemAttr.$item === entries[0].target && itemAttr.$item.classList.contains('appear-animation')) {
                            itemAttr.$item.classList.remove('appear-animation');
                        }
                    });
                }
            }, { threshold: [threshold, mobileThreshold, 0] });

            const itemAttr = {
                $item,
                observer,
            };

            this.items.push(itemAttr);

            DeviceManager.addCallback(attr => {
                if ((enableMobile && !that.desktopInitialized) || (!that.desktopInitialized && DeviceManager.isGreaterThanOrEqual(attr.current, 'md'))) { // Init appear on desktop
                    that.initAppear(itemAttr);
                } else if (!enableMobile && that.desktopInitialized && !DeviceManager.isGreaterThanOrEqual(attr.current, 'md')) { // Destroy appear on mobile
                    that.destroyAppear(itemAttr);
                }
            });
        });
    },

    /**
     * @param {AppearItemType} attr
     * 
     * @return {void}
     */
    initAppear(attr: AppearItemType) : void {
        attr.observer.observe(attr.$item);
    },

    /**
     * @param {AppearItemType} attr
     * 
     * @return {void}
     */
    destroyAppear(attr: AppearItemType) : void {
        attr.$item.classList.remove('appear-animation');
        attr.observer.unobserve(attr.$item);
    },
};

export default Appear;
